import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  getFilteredRowModel,
} from '@tanstack/react-table';
import { CheckCircle2, TrendingUp, Users, Award, ArrowLeft } from 'lucide-react';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useGetCaseStatsAggregationQuery, CertificationStatus } from '@/@generated/codegen/API';
import { useAuthTokenContext } from '@/auth/KeycloakProvider';
import {
  StatsFilters,
  CertificationFilter,
  AccessFilter,
  CompletionFilter,
} from '@/components/stats/StatsFilters';
import { UserDetailsModal } from '@/components/stats/UserDetailsModal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ROUTES, getMasterclassStatsOverviewRoute } from '@/config/routes';
import { useMasterclass } from '@/contexts/MasterclassContext';
import { useTranslations } from '@/hooks/use-translations';
import { useCasesConfig } from '@/modules/case/hooks/useCasesConfig';
import { CERTIFICATION_CONFIG } from '@/modules/certification/constants';

interface UserStats {
  userId: string;
  username: string;
  caseSelectorVisits: number;
  caseSelectorAttempts: number;
  completions: number;
  startDate: string | null;
  nps: number | null;
  scores: string;
  status: 'completed' | 'failed';
  nbrAttempts: number;
  hasAccessed: boolean;
  hasCompleted: boolean;
}

const columnHelper = createColumnHelper<UserStats>();

const columns = [
  columnHelper.accessor('username', {
    header: 'Username',
    cell: (info) => info.getValue() || '--',
  }),
  columnHelper.accessor('caseSelectorVisits', {
    header: 'Visits',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('caseSelectorAttempts', {
    header: 'Attempts',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('completions', {
    header: 'Case completions',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('startDate', {
    header: 'Start Date',
    cell: (info) => {
      const date = info.getValue();
      return date ? new Date(date).toLocaleDateString() : '--';
    },
  }),
  columnHelper.accessor('nps', {
    header: 'NPS',
    cell: (info) => {
      const nps = info.getValue();
      return nps !== null ? `${nps}%` : '--';
    },
  }),
  columnHelper.accessor('scores', {
    header: 'Test scores',
    cell: (info) => {
      const scores = info.getValue();
      return scores ? scores : '--';
    },
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: (info) => {
      const status = info.getValue();
      if (status === 'completed') {
        return (
          <Badge className="bg-livelinx-green text-white hover:bg-livelinx-green">Passed</Badge>
        );
      }
      if (status === 'failed') {
        return <Badge className="bg-livelinx-red text-white hover:bg-livelinx-red">Failed</Badge>;
      }
      return '--';
    },
  }),
  columnHelper.accessor('nbrAttempts', {
    header: 'Test Attempts',
    cell: (info) => {
      const attempts = info.getValue();
      return `${attempts}/${CERTIFICATION_CONFIG.MAX_ATTEMPTS}`;
    },
  }),
];

export default function Stats() {
  const { t } = useTranslations('common');
  const { masterclassId, caseId } = useParams<{ masterclassId: string; caseId: string }>();
  const [searchQuery, setSearchQuery] = useState('');
  const [certificationFilter, setCertificationFilter] = useState<CertificationFilter>('all');
  const [accessFilter, setAccessFilter] = useState<AccessFilter>('all');
  const [completionFilter, setCompletionFilter] = useState<CompletionFilter>('all');
  const [selectedUser, setSelectedUser] = useState<UserStats | null>(null);
  const authContext = useAuthTokenContext();

  // Get masterclass title for breadcrumbs
  const { masterclassTitle } = useCasesConfig({ masterclassId });
  const { setSelectedMasterclass, setMasterclassTitle, setSelectedCase } = useMasterclass();

  // Set context for breadcrumbs
  useEffect(() => {
    if (masterclassId) {
      setSelectedMasterclass(masterclassId);
    }
    if (masterclassTitle) {
      setMasterclassTitle(masterclassTitle);
    }
    if (caseId) {
      setSelectedCase(caseId);
    }
    return () => {
      setSelectedMasterclass(null);
      setMasterclassTitle(null);
      setSelectedCase(null);
    };
  }, [
    masterclassId,
    masterclassTitle,
    caseId,
    setSelectedMasterclass,
    setMasterclassTitle,
    setSelectedCase,
  ]);

  // Fetch aggregated stats from backend
  const { data: statsData, loading: dataLoading } = useGetCaseStatsAggregationQuery({
    variables: {
      caseId: caseId || '',
      masterclassId: masterclassId || '',
    },
    skip: !caseId || !masterclassId,
  });

  // Transform backend data to UI format
  const fullData: UserStats[] = useMemo(() => {
    if (!statsData?.getCaseStatsAggregation?.userStats) return [];

    return statsData.getCaseStatsAggregation.userStats.map((user): UserStats => {
      const hasPassed = user.certificationStatus === CertificationStatus.Passed;

      // Calculate NPS from rating (rating is 0-5, convert to percentage)
      const nps =
        user.averageRating !== null && user.averageRating !== undefined
          ? Math.round((user.averageRating / 5) * 100)
          : null;

      // Format score as percentage
      const scores =
        user.bestCertificationScore !== null && user.bestCertificationScore !== undefined
          ? `${Math.round(user.bestCertificationScore)}%`
          : '';

      return {
        userId: user.userId,
        username: user.username,
        caseSelectorVisits: user.caseSelectorVisits,
        caseSelectorAttempts: user.caseClicks,
        completions: user.completions,
        startDate: user.firstAccessDate ? user.firstAccessDate.toString() : null,
        nps,
        scores,
        status: hasPassed ? 'completed' : 'failed',
        nbrAttempts: user.certificationAttempts,
        hasAccessed: user.hasAccessed,
        hasCompleted: user.hasCompleted,
      };
    });
  }, [statsData]);

  const loading = dataLoading;

  const filteredData = useMemo(() => {
    let result = fullData;

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((user: UserStats) => user.username.toLowerCase().includes(query));
    }

    // Certification filter
    if (certificationFilter !== 'all') {
      result = result.filter((user: UserStats) => user.status === certificationFilter);
    }

    // Access filter
    if (accessFilter === 'accessed') {
      result = result.filter((user: UserStats) => user.hasAccessed);
    } else if (accessFilter === 'not-accessed') {
      result = result.filter((user: UserStats) => !user.hasAccessed);
    }

    // Completion filter
    if (completionFilter === 'completed') {
      result = result.filter((user: UserStats) => user.hasCompleted);
    } else if (completionFilter === 'not-completed') {
      result = result.filter((user: UserStats) => !user.hasCompleted);
    }

    return result;
  }, [fullData, searchQuery, certificationFilter, accessFilter, completionFilter]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const navigate = useNavigate();

  useEffect(() => {
    // Check auth via Keycloak context
    if (authContext.tokenStatus === 'invalid') {
      navigate(ROUTES.AUTH);
    }
  }, [authContext.tokenStatus, navigate]);

  const exportToCSV = () => {
    const headers = [
      t('stats.username'),
      t('stats.visits'),
      t('stats.attempts'),
      t('stats.completions'),
      t('stats.startDate'),
      t('stats.nps'),
      t('stats.testScores'),
      t('stats.status'),
      t('stats.testAttempts'),
      t('stats.hasAccessed'),
      t('stats.hasCompleted'),
    ];

    const csvData = filteredData.map((user: UserStats) => [
      user.username || '--',
      user.caseSelectorVisits,
      user.caseSelectorAttempts,
      user.completions,
      user.startDate ? new Date(user.startDate).toLocaleDateString() : '--',
      user.nps !== null ? `${user.nps}%` : '--',
      user.scores || '--',
      user.status === 'completed' ? t('stats.passed') : t('stats.failed'),
      `${user.nbrAttempts}/${CERTIFICATION_CONFIG.MAX_ATTEMPTS}`,
      user.hasAccessed ? t('stats.yes') : t('stats.no'),
      user.hasCompleted ? t('stats.yes') : t('stats.no'),
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map((row: (string | number | null)[]) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `statistics-${caseId}-${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setCertificationFilter('all');
    setAccessFilter('all');
    setCompletionFilter('all');
  };

  const stats = useMemo(() => {
    const summary = statsData?.getCaseStatsAggregation?.summary;
    return {
      total: summary?.totalUsers || 0,
      passed: summary?.usersWhoPassed || 0,
      accessed: summary?.usersWhoAccessed || 0,
      completed: summary?.usersWhoCompleted || 0,
    };
  }, [statsData]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-lg">{t('stats.loading')}</p>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-auto">
      <div className="container mx-auto px-4 py-4 md:py-10">
        <Button
          variant="outline"
          className="mb-4 border-livelinx-lightgray100 bg-white text-livelinx-purple200 transition-all duration-200 hover:border-livelinx-purple100 hover:bg-livelinx-purple50 hover:text-livelinx-purple200"
          onClick={() =>
            masterclassId
              ? navigate(getMasterclassStatsOverviewRoute(masterclassId))
              : navigate(ROUTES.MASTERCLASS_HOME)
          }
        >
          <ArrowLeft className="mr-2 h-5 w-5" />
          {t('buttons.back')}
        </Button>

        <div className="mb-6">
          <h1 className="mb-2 font-heading text-2xl text-livelinx-purple200 md:text-4xl">
            {t('stats.userStatsTitle')}
          </h1>
          <p className="text-livelinx-grey200">
            {t('stats.case')}: {statsData?.getCaseStatsAggregation?.caseName || caseId}
          </p>
        </div>

        {/* Summary Cards */}
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Card className="shadow-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-livelinx-grey200">{t('stats.total')}</p>
                  <p className="font-heading text-3xl text-livelinx-purple200">{stats.total}</p>
                </div>
                <Users className="h-8 w-8 text-livelinx-blue300" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-livelinx-grey200">{t('stats.passed')}</p>
                  <p className="font-heading text-3xl text-livelinx-purple200">{stats.passed}</p>
                </div>
                <Award className="h-8 w-8 text-livelinx-green" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-livelinx-grey200">{t('stats.accessed')}</p>
                  <p className="font-heading text-3xl text-livelinx-purple200">{stats.accessed}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-livelinx-blue300" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-livelinx-grey200">{t('stats.completed')}</p>
                  <p className="font-heading text-3xl text-livelinx-purple200">{stats.completed}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-livelinx-purple200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="mb-6">
          <StatsFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            certificationFilter={certificationFilter}
            onCertificationFilterChange={setCertificationFilter}
            accessFilter={accessFilter}
            onAccessFilterChange={setAccessFilter}
            completionFilter={completionFilter}
            onCompletionFilterChange={setCompletionFilter}
            onExport={exportToCSV}
            totalCount={fullData.length}
            filteredCount={filteredData.length}
            onClearFilters={clearFilters}
          />
        </div>

        {/* Mobile Card View */}
        <div className="space-y-3 pb-20 md:hidden">
          {filteredData.length > 0 ? (
            filteredData.map((user: UserStats, idx: number) => (
              <Card
                key={idx}
                className="cursor-pointer shadow-white transition-shadow hover:shadow-blue-shadow"
                onClick={() => setSelectedUser(user)}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between gap-2 font-body text-base">
                    <span className="flex-1 truncate">{user.username || '--'}</span>
                    <div className="flex shrink-0 gap-1">
                      {user.status === 'completed' && (
                        <Badge className="bg-livelinx-green text-xs text-white hover:bg-livelinx-green">
                          {t('stats.passed')}
                        </Badge>
                      )}
                      {user.status === 'failed' && (
                        <Badge className="bg-livelinx-red text-xs text-white hover:bg-livelinx-red">
                          {t('stats.failed')}
                        </Badge>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-livelinx-grey200">{t('stats.visits')}:</span>
                      <span className="ml-1 font-body">{user.caseSelectorVisits}</span>
                    </div>
                    <div>
                      <span className="text-livelinx-grey200">{t('stats.completions')}:</span>
                      <span className="ml-1 font-body">{user.completions}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {user.hasAccessed && (
                      <Badge variant="outline" className="bg-livelinx-blue50 text-xs">
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        {t('stats.accessed')}
                      </Badge>
                    )}
                    {user.hasCompleted && (
                      <Badge variant="outline" className="bg-livelinx-green/10 text-xs">
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        {t('stats.completed')}
                      </Badge>
                    )}
                  </div>
                  <div className="pt-2 text-xs text-livelinx-grey200">
                    {t('stats.clickForDetails')}
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="py-8 text-center text-livelinx-grey200">
                {t('stats.noResults')}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden overflow-x-auto rounded-md border md:block">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className="text-center font-heading">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer transition-colors hover:bg-livelinx-purple50"
                    onClick={() => setSelectedUser(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="text-center">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center text-livelinx-grey200"
                  >
                    {t('stats.noResults')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* User Details Modal */}
        <UserDetailsModal
          user={selectedUser}
          isOpen={!!selectedUser}
          onClose={() => setSelectedUser(null)}
        />
      </div>
    </div>
  );
}
